import { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  ArrowUpRight,
  Building2,
  Link2,
  LoaderCircle,
  Stethoscope,
  Users,
  UserPlus,
} from "lucide-react";
import { getUserDetails } from "../../../../Service/userAQapi";
import Loader from "../../../../reusableUI/loader";

const TIMEFRAME_OPTIONS = [
  { label: "7D", value: "last7days" },
  { label: "15D", value: "last15days" },
  { label: "30D", value: "last30days" },
  { label: "All time", value: "alltime" },
];

const THEME_PRIMARY = "#4951EC";
const THEME_PRIMARY_SOFT = "#EEF0FF";

const DEFAULT_DATA = {
  timePeriod: "alltime",
  overview: {
    totalRegistrations: 0,
    patientRegistrations: 0,
    specialistRegistrations: 0,
    selfOnboardedSpecialistRegistrations: 0,
    paidSpecialistRegistrations: 0,
    specialistConversionRate: 0,
    partnerSourcedRegistrations: 0,
  },
  trend: {
    granularity: "day",
    categories: [],
    series: [],
  },
  sourceMix: {
    counts: {
      self: 0,
      hospital: 0,
      agency: 0,
      unknown: 0,
    },
    breakdown: [],
    topPartners: [],
  },
  funnel: {
    specialists: 0,
    selfSpecialists: 0,
    paidSpecialists: 0,
    specialistConversionRate: 0,
  },
  cohorts: [],
  roleMix: [],
  referrals: {
    usersWithReferralCode: 0,
    referredUsers: 0,
    referredPaidUsers: 0,
    referralConversionRate: 0,
  },
  insights: [],
  meta: {
    downloadsAvailable: false,
  },
};

const countFormatter = new Intl.NumberFormat("en-IN");

const formatCount = (value) => countFormatter.format(Number(value || 0));

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const getDisplaySourceLabel = (entry) => {
  const key = String(entry?.key || entry?.type || "").trim().toLowerCase();
  const label = String(entry?.label || entry?.type || "").trim();

  if (key === "unknown") {
    return "Independent";
  }

  if (label.toLowerCase() === "unknown") {
    return "Independent";
  }

  return label || "Independent";
};

const getDisplayRoleLabel = (item) => {
  const label = String(item?.label || "").trim();
  return label.toLowerCase() === "unknown" ? "Individual" : label;
};

const sanitizeInsightText = (text) =>
  String(text || "")
    .replace(/\bUnknown\b/g, "Independent")
    .replace(/\bunknown\b/g, "independent");

const getSourceTone = (key) => {
  switch (key) {
    case "self":
      return {
        dot: THEME_PRIMARY,
        badge: "bg-[#EEF0FF] text-[#4951EC]",
      };
    case "hospital":
      return {
        dot: THEME_PRIMARY,
        badge: "bg-[#EEF0FF] text-[#4951EC]",
      };
    case "agency":
      return {
        dot: "#F97316",
        badge: "bg-[#FFEDD5] text-[#C2410C]",
      };
    default:
      return {
        dot: "#64748B",
        badge: "bg-slate-100 text-slate-600",
      };
  }
};

const panelClassName =
  "h-full rounded-[28px] border border-white/80 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)]";

const StatCard = ({ icon: Icon, eyebrow, value, detail, accentClass }) => (
  <div className="rounded-[24px] border border-white/80 bg-white p-5 shadow-[0_16px_45px_rgba(15,23,42,0.08)]">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {eyebrow}
        </p>
        <p className="mt-3 text-[32px] font-semibold tracking-[-0.04em] text-slate-900">
          {value}
        </p>
      </div>
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-2xl ${accentClass}`}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <p className="text-sm leading-6 text-slate-500">{detail}</p>
  </div>
);

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="mb-5 flex items-start justify-between gap-4">
    <div>
      <h3 className="text-lg font-semibold tracking-[-0.03em] text-slate-900">
        {title}
      </h3>
      {subtitle ? (
        <p className="mt-1 text-sm leading-6 text-slate-500">{subtitle}</p>
      ) : null}
    </div>
    {action}
  </div>
);

const TrendChart = ({ trend }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        backgroundColor: "transparent",
        height: 248,
        spacingTop: 8,
        spacingLeft: 0,
        spacingRight: 0,
      },
      title: { text: null },
      xAxis: {
        categories: trend.categories,
        lineColor: "#D7E4E1",
        tickColor: "#D7E4E1",
        labels: {
          style: {
            color: "#64748B",
            fontSize: "11px",
          },
        },
      },
      yAxis: {
        allowDecimals: false,
        title: { text: null },
        gridLineColor: "#E2ECE9",
        labels: {
          style: {
            color: "#64748B",
            fontSize: "11px",
          },
        },
      },
      legend: {
        align: "left",
        verticalAlign: "top",
        itemStyle: {
          color: "#334155",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: "#0F172A",
        borderWidth: 0,
        style: {
          color: "#F8FAFC",
        },
      },
      plotOptions: {
        area: {
          stacking: "normal",
          lineWidth: 2,
          fillOpacity: 0.16,
          marker: {
            enabled: false,
          },
        },
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      series: trend.series.map((series) => ({
        name: series.name,
        data: series.data,
        color: series.color,
      })),
      credits: {
        enabled: false,
      },
    }),
    [trend]
  );

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const SourceMixChart = ({ sourceMix }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 220,
        spacing: [0, 0, 0, 0],
      },
      title: { text: null },
      tooltip: {
        pointFormat: "<b>{point.y}</b> registrations ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "72%",
          borderWidth: 0,
          dataLabels: {
            enabled: false,
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      series: [
        {
          name: "Registrations",
          data: sourceMix.breakdown.map((entry) => ({
            name: getDisplaySourceLabel(entry),
            y: entry.count,
            color: getSourceTone(entry.key).dot,
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [sourceMix]
  );

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const RoleMixChart = ({ roleMix }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        backgroundColor: "transparent",
        height: 220,
      },
      title: { text: null },
      xAxis: {
        categories: roleMix.map((item) => getDisplayRoleLabel(item)),
        lineColor: "#D7E4E1",
        labels: {
          style: {
            color: "#475569",
            fontSize: "12px",
          },
        },
      },
      yAxis: {
        allowDecimals: false,
        title: { text: null },
        gridLineColor: "#E2ECE9",
        labels: {
          style: {
            color: "#64748B",
            fontSize: "11px",
          },
        },
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          borderRadius: 10,
          pointPadding: 0.14,
        },
      },
      series: [
        {
          data: roleMix.map((item) => ({
            y: item.count,
            color: item.color,
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [roleMix]
  );

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const FunnelPanel = ({ funnel }) => {
  const stages = [
    {
      label: "All specialist signups",
      value: funnel.specialists,
      color: "bg-[#CBD5E1]",
    },
    {
      label: "Self specialist signups",
      value: funnel.selfSpecialists,
      color: "bg-[#4951EC]",
    },
    {
      label: "Paid self specialists",
      value: funnel.paidSpecialists,
      color: "bg-[#F97316]",
    },
  ];
  const maxValue = Math.max(...stages.map((stage) => stage.value), 1);

  return (
    <div className={panelClassName}>
      <SectionHeader
        title="Self-onboarding conversion"
        subtitle="Payment conversion only for self-onboarded specialists."
      />
      <div className="space-y-4">
        {stages.map((stage) => (
          <div key={stage.label}>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium text-slate-700">{stage.label}</span>
              <span className="font-semibold text-slate-900">
                {formatCount(stage.value)}
              </span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full ${stage.color}`}
                style={{ width: `${(stage.value / maxValue) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl bg-slate-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
          Self specialist conversion
        </p>
        <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
          {formatPercent(funnel.specialistConversionRate)}
        </p>
      </div>
    </div>
  );
};

const CohortPanel = ({ cohorts }) => (
  <div className={panelClassName}>
    <SectionHeader
      title="Recent cohorts"
      subtitle="Rolling windows to compare self-onboarded specialist payment conversion."
    />
    <div className="overflow-x-auto rounded-2xl border border-slate-100">
      <table className="min-w-full divide-y divide-slate-100">
        <thead className="bg-slate-50">
          <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-400">
            <th className="px-4 py-3 font-semibold">Window</th>
            <th className="px-4 py-3 font-semibold">Specs</th>
            <th className="px-4 py-3 font-semibold">Self</th>
            <th className="px-4 py-3 font-semibold">Paid</th>
            <th className="px-4 py-3 font-semibold">Rate</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {cohorts.map((cohort) => (
            <tr key={cohort.label} className="text-sm text-slate-600">
              <td className="px-4 py-3 font-semibold text-slate-900">
                {cohort.label}
              </td>
              <td className="px-4 py-3">{formatCount(cohort.specialists)}</td>
              <td className="px-4 py-3">{formatCount(cohort.selfSpecialists)}</td>
              <td className="px-4 py-3">{formatCount(cohort.paidSpecialists)}</td>
              <td className="px-4 py-3 font-medium text-slate-900">
                {formatPercent(cohort.conversionRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ReferralPanel = ({ referrals }) => {
  const showZeroState = referrals.referredUsers === 0;

  return (
    <div className={panelClassName}>
      <SectionHeader
        title="Referral effectiveness"
        subtitle="How referral-enabled acquisition contributes to paid onboarding."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            Codes generated
          </p>
          <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
            {formatCount(referrals.usersWithReferralCode)}
          </p>
        </div>
        <div className="rounded-2xl bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            Referred users
          </p>
          <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
            {formatCount(referrals.referredUsers)}
          </p>
        </div>
        <div className="rounded-2xl bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            Paid referred users
          </p>
          <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
            {formatCount(referrals.referredPaidUsers)}
          </p>
        </div>
        <div className="rounded-2xl bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            Referral conversion
          </p>
          <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
            {formatPercent(referrals.referralConversionRate)}
          </p>
        </div>
      </div>
      <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-4 text-sm leading-6 text-slate-500">
        {showZeroState
          ? "No referral-driven registrations landed in this timeframe yet. The panel stays visible so quiet periods are explicit rather than hidden."
          : "Referral activity is present in this window, so the card surfaces both the reach of referral codes and the downstream paid conversion."}
      </div>
    </div>
  );
};

const SourcePanel = ({ sourceMix, totalRegistrations }) => (
  <div className={panelClassName}>
    <SectionHeader
      title="Acquisition source mix"
      subtitle="Where recent registrations are being sourced from."
    />
    <div className="grid grid-cols-1 items-center gap-4 min-[1100px]:grid-cols-[1.1fr_0.9fr]">
      <div>
        <SourceMixChart sourceMix={sourceMix} />
      </div>
      <div className="space-y-3">
        {sourceMix.breakdown.map((entry) => {
          const tone = getSourceTone(entry.key);
          return (
            <div
              key={entry.key}
              className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: tone.dot }}
                  />
                  <span className="font-medium text-slate-700">
                    {getDisplaySourceLabel(entry)}
                  </span>
                </div>
                <span className="text-sm font-semibold text-slate-900">
                  {formatCount(entry.count)}
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${
                      totalRegistrations
                        ? (entry.count / totalRegistrations) * 100
                        : 0
                    }%`,
                    backgroundColor: tone.dot,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <div className="mt-6">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900">Top partners</h4>
        <span className="text-xs uppercase tracking-[0.16em] text-slate-400">
          attributed
        </span>
      </div>
      {sourceMix.topPartners.length ? (
        <div className="max-h-[20rem] space-y-3 overflow-y-auto pr-1">
          {sourceMix.topPartners.map((partner) => {
            const tone = getSourceTone(partner.type);
            return (
              <div
                key={`${partner.type}-${partner.name}`}
                className="flex items-center justify-between rounded-2xl border border-slate-100 px-4 py-3"
              >
                <div>
                  <p className="font-medium text-slate-900">{partner.name}</p>
                  <span
                    className={`mt-2 inline-flex rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${tone.badge}`}
                  >
                    {getDisplaySourceLabel(partner)}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900">
                    {formatCount(partner.count)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatPercent(partner.share)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm leading-6 text-slate-500">
          No partner attribution in this period.
        </div>
      )}
    </div>
  </div>
);

const InsightsPanel = ({ insights, timePeriod }) => (
  <div className={panelClassName}>
    <SectionHeader
      title="Operational readout"
      subtitle="Deterministic takeaways generated from the current summary."
      action={
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          {timePeriod}
        </span>
      }
    />
    <div className="space-y-3">
      {insights.map((insight, index) => (
        <div
          key={`${index}-${insight}`}
          className="flex gap-3 rounded-2xl bg-slate-50 px-4 py-4"
        >
          <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-500">
            {index + 1}
          </div>
          <p className="text-sm leading-6 text-slate-600">
            {sanitizeInsightText(insight)}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const TrendSummaryStrip = ({ overview, funnel, sourceMix }) => {
  const leadingSource = [...(sourceMix?.breakdown || [])]
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    })
    .find((item) => item.count > 0);

  const items = [
    {
      label: "Patients",
      value: formatCount(overview.patientRegistrations),
      accent: "text-[#4951EC]",
    },
    {
      label: "Self specialists",
      value: formatCount(overview.selfOnboardedSpecialistRegistrations),
      accent: "text-[#4951EC]",
    },
    {
      label: "Paid self specialists",
      value: formatCount(funnel.paidSpecialists),
      accent: "text-[#F97316]",
    },
    {
      label: "Leading source",
      value: leadingSource
        ? `${getDisplaySourceLabel(leadingSource)} (${formatCount(leadingSource.count)})`
        : "No source",
      accent: "text-slate-700",
    },
  ];

  return (
    <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {item.label}
          </p>
          <p
            className={`mt-2 text-lg font-semibold tracking-[-0.03em] ${item.accent}`}
          >
            {item.value}
          </p>
        </div>
      ))}
    </div>
  );
};

const DashboardPage = () => {
  const [timeframe, setTimeframe] = useState("alltime");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(DEFAULT_DATA);

  useEffect(() => {
    let isCancelled = false;

    const fetchUserAcquisition = async () => {
      const isInitialLoad = !userData?.meta?.generatedAt;
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError("");

      try {
        const result = await getUserDetails(timeframe);
        if (isCancelled) return;

        if (!result?.error && result?.data) {
          setUserData({
            ...DEFAULT_DATA,
            ...result.data,
          });
        } else {
          setError("Unable to load acquisition insights right now.");
        }
      } catch (fetchError) {
        if (!isCancelled) {
          setError("Unable to load acquisition insights right now.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    fetchUserAcquisition();

    return () => {
      isCancelled = true;
    };
  }, [timeframe]);

  const overview = userData?.overview || DEFAULT_DATA.overview;
  const trend = userData?.trend || DEFAULT_DATA.trend;
  const sourceMix = userData?.sourceMix || DEFAULT_DATA.sourceMix;
  const funnel = userData?.funnel || DEFAULT_DATA.funnel;
  const cohorts = userData?.cohorts || DEFAULT_DATA.cohorts;
  const roleMix = userData?.roleMix || DEFAULT_DATA.roleMix;
  const referrals = userData?.referrals || DEFAULT_DATA.referrals;
  const insights = userData?.insights?.length
    ? userData.insights
    : DEFAULT_DATA.insights;

  const totalRegistrations = overview.totalRegistrations || 0;

  const cards = [
    {
      eyebrow: "New registrations",
      value: formatCount(overview.totalRegistrations),
      detail: `${formatCount(
        overview.patientRegistrations
      )} patients and ${formatCount(
        overview.specialistRegistrations
      )} specialists in this window.`,
      icon: UserPlus,
      accentClass: "bg-[#EEF0FF] text-[#4951EC]",
    },
    {
      eyebrow: "Specialist signups",
      value: formatCount(overview.specialistRegistrations),
      detail: "Nurse, physiotherapist, and other non-patient registrations.",
      icon: Stethoscope,
      accentClass: "bg-[#EEF0FF] text-[#4951EC]",
    },
    {
      eyebrow: "Paid specialist conversions",
      value: formatCount(overview.paidSpecialistRegistrations),
      detail: `${formatPercent(
        overview.specialistConversionRate
      )} conversion across ${formatCount(
        overview.selfOnboardedSpecialistRegistrations
      )} self-onboarded specialist signups.`,
      icon: ArrowUpRight,
      accentClass: "bg-[#FFEDD5] text-[#F97316]",
    },
    {
      eyebrow: "Partner-sourced registrations",
      value: formatCount(overview.partnerSourcedRegistrations),
      detail: "Registrations attributed to hospitals, agencies, or other partner-led sources.",
      icon: Building2,
      accentClass: "bg-slate-100 text-slate-700",
    },
  ];

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="h-[calc(100vh-8.5rem)] overflow-y-auto overflow-x-hidden bg-[#EEF4F1] px-3 pb-12 pt-3 md:px-4">
      <div className="mx-auto max-w-[1480px]">
        <div className="rounded-[30px] border border-white/80 bg-white/70 px-5 py-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm md:px-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF0FF] text-[#4951EC]">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Growth analytics
                  </p>
                  <h1 className="mt-1 text-[32px] font-semibold tracking-[-0.05em] text-slate-950">
                    User Acquisition
                  </h1>
                </div>
              </div>
              {/* <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-500">
                How new users arrive, which channels are producing registrations,
                and how specialist onboarding converts into paid activation.
              </p> */}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="inline-flex rounded-2xl bg-slate-100 p-1">
                {TIMEFRAME_OPTIONS.map((option) => {
                  const isActive = timeframe === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTimeframe(option.value)}
                      className={`rounded-[14px] px-4 py-2 text-sm font-medium transition ${
                        isActive
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-400"
              >
                <Link2 className="h-4 w-4" />
                Export soon
              </button>
            </div>
          </div>

          {refreshing ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
              <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
              Updating dashboard
            </div>
          ) : null}

          {error ? (
            <div className="mt-5 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
              {error}
            </div>
          ) : null}
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-4">
          {cards.map((card) => (
            <StatCard key={card.eyebrow} {...card} />
          ))}
        </div>

        <div className="mt-5 grid items-stretch gap-4 min-[1100px]:grid-cols-12">
          <div className={`min-[1100px]:col-span-8 ${panelClassName}`}>
            <SectionHeader
              title="Registrations over time"
              subtitle="Role-level registration flow for the selected timeframe."
            />
            {trend.categories.length ? (
              <>
                <TrendChart trend={trend} />
                <TrendSummaryStrip
                  overview={overview}
                  funnel={funnel}
                  sourceMix={sourceMix}
                />
              </>
            ) : (
              <div className="flex h-[320px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                No registration activity to chart in this period.
              </div>
            )}
          </div>

          <div className="min-[1100px]:col-span-4 h-full">
            <SourcePanel
              sourceMix={sourceMix}
              totalRegistrations={totalRegistrations}
            />
          </div>
        </div>

        <div className="mt-5 grid items-stretch gap-4 min-[1100px]:grid-cols-12">
          <div className="min-[1100px]:col-span-4 h-full">
            <FunnelPanel funnel={funnel} />
          </div>
          <div className="min-[1100px]:col-span-5 h-full">
            <CohortPanel cohorts={cohorts} />
          </div>
          <div className="min-[1100px]:col-span-3 h-full">
            <div className={panelClassName}>
              <SectionHeader
                title="Role mix"
                subtitle="Distribution of registrations across user types."
              />
              {roleMix.some((item) => item.count > 0) ? (
                <RoleMixChart roleMix={roleMix} />
              ) : (
                <div className="flex h-[280px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                  No role mix to show yet.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 grid items-stretch gap-4 min-[1100px]:grid-cols-12">
          <div className="min-[1100px]:col-span-6 h-full">
            <ReferralPanel referrals={referrals} />
          </div>
          <div className="min-[1100px]:col-span-6 h-full">
            <InsightsPanel insights={insights} timePeriod={userData.timePeriod} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
